import { flash, Modal } from "@xola/ui-kit";
import { useCounter, useMemoizedFn, useMount } from "ahooks";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef } from "react";
import { CanvasLocation, CanvasPresentation, CanvasType, Plugin, TriggerType } from "types";
import { BrokenAppPlaceholder } from "./BrokenAppPlaceholder";
import { canvasSizePx } from "./constants";
import { EmbeddedAppChrome } from "./EmbeddedAppChrome";
import { DrawerCloseButton, EmbeddedAppDrawer } from "./EmbeddedAppDrawer";
import { useDynamicCanvasConfig, useEmbeddedAppStore, useIsAppBroken } from "./embeddedAppStore";
import * as hostRpc from "./hostRpc";
import { AppStatus, useEmbeddedAppSession } from "./useEmbeddedAppSession";
import { useIframePlacement } from "./useIframePlacement";
import { useInstalledPluginsFromStore } from "@/stores/globalStore";

// Off-screen instead of display:none - display:none gives 0x0 viewport and cross-origin
// apps that measure layout on init will stay blank after being moved into the canvas.
const HIDDEN_ROOT_CLASS = "fixed -left-[9999px] top-0 h-screen overflow-hidden invisible pointer-events-none";

export const EmbeddedAppRoot = () => {
    const plugins = useInstalledPluginsFromStore();
    const apps = useMemo(
        // fullscreen apps render via their own route
        () => plugins.filter((p) => p.ui?.isEmbeddedApp && p.ui.canvasType !== CanvasType.FULLSCREEN),
        [plugins],
    );

    return (
        <>
            {apps.map((plugin) => (
                <EmbeddedAppInstance key={plugin.id} plugin={plugin} />
            ))}
        </>
    );
};

const EmbeddedAppInstance = ({ plugin }: { plugin: Plugin }) => {
    const { id: appId, name: appName, ui } = plugin;
    const {
        canvasType = CanvasType.DRAWER,
        triggers,
        canvasSize,
        canvasLocation = CanvasLocation.RIGHT,
        canvasPresentation = CanvasPresentation.OVERLAY,
    } = ui ?? {};

    const [retryKey, { inc: incRetryKey }] = useCounter(0);
    const actions = useEmbeddedAppStore.getState().actions;
    const onClose = useMemoizedFn(() => {
        hostRpc.notifyCanvasClose(appId, canvasType === CanvasType.MODAL ? "modal" : "drawer");
        actions.closeCanvas(appId);
    });

    const handleRetry = useMemoizedFn(() => {
        actions.resetBroken(appId);
        incRetryKey();
    });

    const handleMarkBroken = useMemoizedFn(() => actions.markBroken(appId));

    // handleLoad is a stable useMemoizedFn, so these consumers need no memoization.
    const { iframeRef, handleLoad, status, isLongWaking, isCanvasOpen } = useEmbeddedAppSession({
        plugin,
        onAppBroken: handleMarkBroken,
        onHandshakeFailed: handleMarkBroken,
        onReload: () => actions.resetBroken(appId),
        onCanvasHide: () => actions.hideDynamicCanvas(appId),
        onCanvasShow: (_id, config) => actions.showDynamicCanvas(appId, config),
    });

    const { iframeRootRef, contentSlotRef, contentSlotCallbackRef, isBroken } = useIframePlacement({
        plugin,
        isCanvasOpen,
        retryKey,
        iframeRef,
        handleLoad,
    });

    useMount(() => {
        if (triggers?.some((t) => t.type === TriggerType.AUTOMATIC) && canvasType !== CanvasType.NONE) {
            actions.openCanvas(appId);
        }
    });

    useEffect(() => {
        if (canvasType === CanvasType.DRAWER) {
            return hostRpc.onCanvasEvent(appId, "drawer:close", onClose);
        }
        if (canvasType === CanvasType.MODAL) {
            return hostRpc.onCanvasEvent(appId, "modal:close", onClose);
        }
    }, [appId, canvasType, onClose]);

    // Surface broken background apps as a toast (no visible canvas to show placeholder in)
    const isBrokenBackgroundApp = isBroken && canvasType === CanvasType.NONE;
    useEffect(() => {
        if (isBrokenBackgroundApp) {
            flash.danger(`Background app "${appName}" failed to load.`);
        }
    }, [isBrokenBackgroundApp, appName]);

    const hiddenRootWidth = canvasSizePx(canvasSize);
    if (canvasType === CanvasType.NONE) {
        return (
            <>
                <div ref={iframeRootRef} className={HIDDEN_ROOT_CLASS} style={{ width: hiddenRootWidth }} />
                <DynamicCanvasHost isBroken={isBroken} appId={appId} contentSlotRef={contentSlotRef} />
            </>
        );
    }

    const canvasContent = (
        <EmbeddedAppCanvas
            isLongWaking={isLongWaking}
            appId={appId}
            appName={appName}
            status={status}
            contentSlotCallbackRef={contentSlotCallbackRef}
            onRetry={handleRetry}
        />
    );

    if (canvasType === CanvasType.MODAL) {
        return (
            <>
                <div ref={iframeRootRef} className={HIDDEN_ROOT_CLASS} style={{ width: hiddenRootWidth }} />
                <Modal isOpen={isCanvasOpen} onClose={onClose}>
                    <Modal.Body className="h-full !p-0">{canvasContent}</Modal.Body>
                </Modal>
            </>
        );
    }

    return (
        <>
            <div ref={iframeRootRef} className={HIDDEN_ROOT_CLASS} style={{ width: hiddenRootWidth }} />
            <EmbeddedAppDrawer
                isOpen={isCanvasOpen}
                canvasLocation={canvasLocation}
                canvasPresentation={canvasPresentation}
                canvasSize={canvasSize}
                onClose={onClose}
            >
                {canvasContent}
            </EmbeddedAppDrawer>
        </>
    );
};

interface EmbeddedAppCanvasProps {
    isLongWaking: boolean;
    appId: string;
    appName: string;
    status: AppStatus;
    contentSlotCallbackRef: (node: HTMLDivElement | null) => void;
    onRetry: () => void;
}

const EmbeddedAppCanvas = React.memo(
    ({ isLongWaking, appId, appName, status, contentSlotCallbackRef, onRetry }: EmbeddedAppCanvasProps) => {
        const isBroken = useIsAppBroken(appId);
        const displayStatus = isBroken ? "broken" : status;

        return (
            <div className="flex h-full flex-col">
                <EmbeddedAppChrome
                    isLongWaking={isLongWaking}
                    appName={appName}
                    status={displayStatus}
                    onReload={onRetry}
                />
                <div className="min-h-0 flex-1">
                    {isBroken ? (
                        <BrokenAppPlaceholder appId={appId} onRetry={onRetry} />
                    ) : (
                        <div ref={contentSlotCallbackRef} className="h-full w-full" />
                    )}
                </div>
            </div>
        );
    },
);

interface DynamicCanvasHostProps {
    isBroken: boolean;
    appId: string;
    contentSlotRef: React.RefObject<HTMLDivElement>;
}

// Hosts a canvasType=none app's dynamic canvas. Panel size and position come from the
// ui.canvas.show RPC payload, so styles are applied directly rather than mapped to classes.
const DynamicCanvasHost = React.memo(({ isBroken, appId, contentSlotRef }: DynamicCanvasHostProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dynamicConfig = useDynamicCanvasConfig(appId);
    const isOpen = !!dynamicConfig;

    const actions = useEmbeddedAppStore.getState().actions;
    const onClose = useMemoizedFn(() => {
        // Dynamic (canvasType=none) canvases are always shown as a drawer (see CanvasConfig).
        hostRpc.notifyCanvasClose(appId, "drawer");
        actions.hideDynamicCanvas(appId);
    });

    useEffect(() => {
        const element = wrapperRef.current;
        if (!element) {
            return;
        }
        if (!dynamicConfig) {
            element.style.cssText = "display: none;";
            return;
        }

        const widthPx = canvasSizePx(dynamicConfig.canvasSize);
        const isLeft = dynamicConfig.canvasLocation === CanvasLocation.LEFT;
        element.style.cssText =
            [
                "position: fixed",
                "top: 0",
                "bottom: 0",
                isLeft ? "left: 0" : "right: 0",
                `width: min(${widthPx}px, 100vw)`,
                "z-index: 20",
                "background: white",
                "box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
            ].join("; ") + ";";
    }, [dynamicConfig]);

    if (isBroken) {
        return null;
    }

    return (
        <>
            {isOpen && dynamicConfig.canvasPresentation !== CanvasPresentation.PUSH && (
                <div className="fixed inset-0 z-[19] bg-black/20" onClick={onClose} />
            )}
            <div ref={wrapperRef} style={{ display: "none" }}>
                <div ref={contentSlotRef} className="h-full w-full" />
                {isOpen && (
                    <div
                        className={clsx(
                            "absolute top-0",
                            dynamicConfig.canvasLocation === CanvasLocation.LEFT ? "-right-[52px]" : "-left-[52px]",
                        )}
                    >
                        <DrawerCloseButton onClick={onClose} />
                    </div>
                )}
            </div>
        </>
    );
});
